import { SortDirection } from "../../presentation/components/GenericTable/Models/sortDirection";

export interface PageQuery {
  searchTerm: string;
  filterBy: FilterBy[];
  sortBy: {
    field: string;
    sortDirection?: SortDirection;
  };
  page: {
    size: number;
    index: number;
  };
  /**
   * "Give me every row" (vendor list, flag REACT_APP_VENDOR_LIST_PAGE_SIZE_ENABLED):
   * emitted as `all=true`. Only ever set by vendorListQuery.ts with the flag
   * on, so an absent/false value leaves the query string byte-identical.
   */
  all?: boolean;
  open?: {
    id: number;
    organizationId: number;
  };
}

export interface FilterBy {
  field: string;
  value: string;
}

export function toQueryParam(queryObj: PageQuery): string {
  const querySegments = [];
  if (queryObj.page?.size) querySegments.push(`pageSize=${queryObj.page.size}`);
  if (queryObj.page?.index)
    querySegments.push(`pageIndex=${queryObj.page.index}`);
  if (queryObj.all === true) querySegments.push("all=true");
  // Encode: search terms are free text, so '&', '#' and '+' would otherwise
  // break the query string ('+' is read as a space server-side). Callers pass
  // the raw term; this is the only place it is encoded (no double encoding).
  if (queryObj.searchTerm)
    querySegments.push(`searchTerm=${encodeURIComponent(queryObj.searchTerm)}`);
  if (queryObj.sortBy?.field)
    querySegments.push(`sortByField=${queryObj.sortBy.field}`);
  if (queryObj.sortBy?.sortDirection)
    querySegments.push(`sortByDirection=${queryObj.sortBy.sortDirection}`);
  if (queryObj.filterBy && queryObj.filterBy.length > 0) {
    for (const filter of queryObj.filterBy) {
      if (!filter.field) continue;
      querySegments.push(`filterByField=${filter.field}`);
      querySegments.push(`filterByValue=${filter.value}`);
    }
  }

  let queryString: string = "";
  for (let i = 0; i < querySegments.length; i++) {
    let symbol;
    if (i === 0) {
      symbol = "?";
    } else {
      symbol = "&";
    }

    queryString += symbol + querySegments[i];
  }

  return queryString;
}

export function toInAppQueryParam(queryObj: PageQuery): string {
  const querySegments = [];
  if (queryObj.page?.size) querySegments.push(`pageSize=${queryObj.page.size}`);
  if (queryObj.page?.index)
    querySegments.push(`pageIndex=${queryObj.page.index}`);
  // Encode free-text search (see toQueryParam). These strings are passed to
  // react-router's navigate() as-is and later parsed with URLSearchParams,
  // which decodes exactly once - so the round trip is loss-free.
  if (queryObj.searchTerm)
    querySegments.push(`searchTerm=${encodeURIComponent(queryObj.searchTerm)}`);
  if (queryObj.sortBy?.field)
    querySegments.push(`sortByField=${queryObj.sortBy.field}`);
  if (queryObj.sortBy?.sortDirection)
    querySegments.push(`sortByDirection=${queryObj.sortBy.sortDirection}`);
  if (queryObj.filterBy && queryObj.filterBy.length > 0) {
    for (const filter of queryObj.filterBy) {
      if (!filter.field) continue;
      querySegments.push(`filterOn=${filter.value}`);
      querySegments.push(`filterBy=${filter.field}`);
    }
  }

  let queryString: string = "";
  for (let i = 0; i < querySegments.length; i++) {
    let symbol;
    if (i === 0) {
      symbol = "?";
    } else {
      symbol = "&";
    }

    queryString += symbol + querySegments[i];
  }

  return queryString;
}
